<template>
  <vk-modal v-if="showModal" @close="closeModal">
    <component
        v-for="(requestComponent, index) in requestComponents"
        v-if="requestComponent === `${activeStep}Modal`"
        :key="index"
        :is="requestComponent"
        :service="selectedService || {}"
        :results="newList || []"
        :locations="locations || []"
        :selected-location="selectedLocation || {}"
        :query="selectedQuery || ''"
        :hierarchy="selectedHierarchy || {}"
        :pricing-data="pricingData"
        @close="closeModal"
    />
  </vk-modal>
</template>
<script>
import { mapGetters } from 'vuex';
import { getServiceAvailability } from '@homeservices/api-kit/servicecatalog/availability';
import { getMatches } from '@homeservices/api-kit/search/promatch';
import { getSubtasks } from '@homeservices/api-kit/search/subtasks/v3';

import http from '@homeservices/api-kit/axios';
import ZipcodeModal from './components/ZipcodeModal';
import UnavailableModal from './components/UnavailableModal';
import AvailableModal from './components/AvailableModal';
import SearchModal from './components/SearchModal';
import HierarchyModal from './components/HierarchyModal';
import SpinnerModal from './components/SpinnerModal';
import { REMOVED_SERVICE_IDS } from '../H2Hero/constants';

export default {
  name: 'services-modal',
  pricingData: '',
  components: {
    SearchModal,
    HierarchyModal,
    ZipcodeModal,
    UnavailableModal,
    AvailableModal,
    SpinnerModal,
  },
  data () {
    return {
      checkedAvailability: false,
      pricingData: '',
      newList: [],
      requestComponents: [
        'SearchModal',
        'HierarchyModal',
        'ZipcodeModal',
        'AvailableModal',
        'UnavailableModal',
        'SpinnerModal',
      ],
    };
  },
  computed: {
    ...mapGetters('homepage/geo', [
      'locations',
      'selectedLocation',
    ]),
    ...mapGetters('homepage/request', [
      'selectedService',
      'selectedServiceAvailable',
    ]),
    ...mapGetters('homepage/hierarchies', [
      'selectedHierarchy',
    ]),
    ...mapGetters('homepage/search', [
      'selectedQuery',
      'results',
    ]),
    activeStep () {
      if (!this.checkedAvailability) { // Haven't checked for availability yet
        if (this.selectedService && !this.selectedLocation) { // When the user selects a service
          return 'Zipcode';
        } if (this.selectedQuery && !this.selectedLocation) { // User searched for a service
          return 'Search';
        } if (this.selectedHierarchy && !this.selectedLocation) { // User selected ViewAll
          return 'Hierarchy';
        }
        return 'Spinner';
      } if (this.selectedLocation) {
        if (this.selectedServiceAvailable) {
          return 'Available';
        }
        return 'Unavailable';
      }
    },
    showModal () {
      return (!!this.selectedService || !!this.selectedHierarchy || this.selectedQuery);
    },
  },
  watch: {

    selectedLocation (newSelectedLocation) {
      if (newSelectedLocation) {
        this.checkAvailability(newSelectedLocation.zipcode);
      }
    },
    async selectedService (value = {}) {
      let external_URL = value.external_url;

      // If no externa_url key, then we entered via service card list and need to do a search call
      if (!('external_url' in value)) {
        external_URL = await this.checkForExternalURL(value.service_display_name);
        // Downstream logic is expecting the service_id at id, so adjusting here as well
        // eslint-disable-next-line no-param-reassign
        value.id = value.service_id;
      }
      if (external_URL) {
        this.$router.push({
          name: 'redirect',
          query: {
            redirect_url: external_URL,
          },
        });
      }
    },
    activeStep (newStep) {
      let trackingContext = {};
      switch (newStep) {
        case 'Zipcode':
          trackingContext = {
            ...this.selectedService,
          };
          break;

        case 'Search':
          trackingContext = {
            ...this.selectedQuery,
            results: this.results.map(item => item.id),
          };
          break;

        case 'Hierarchy':
          trackingContext = {
            ...this.selectedHierarchy,
          };
          break;

        case 'Available':
          trackingContext = {
            ...this.selectedService,
            ...this.selectedLocation,
            ...this.pricingData,
          };
          break;

        case 'Unavailable':
          trackingContext = {
            ...this.selectedService,
            ...this.selectedLocation,
          };
          break;

        default:
          break;
      }
      trackingContext.step = newStep;
      this.$events.$emit(this.$events.HOMEPAGE_MODAL, trackingContext);
    },
    results (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setList();
      }
    },
  },
  methods: {
    setList () {
      const serviceIds = REMOVED_SERVICE_IDS;
      this.newList = this.results.filter(({ service_identifier }) =>
        !serviceIds.includes(service_identifier));
      return this.newList;
    },
    checkAvailability: async function checkAvailability (zipcode) {
      const serviceIdentifier = this.selectedService.service_identifier;
      const { data: results } = await getServiceAvailability({
        zipcode,
        service_identifier: serviceIdentifier,
      });
      const { data } = await http.get('/platform/servicecatalog/api/servicecatalog/v1/pricing/v1', {
        params: {
          zipcode,
          service_identifier: serviceIdentifier,
        },
      });
      this.pricingData = data || '';

      this.$emit('checkAvailability', results);

      let available = 0;
      if (results.count > 0) {
        try {
          const { data: {
            count,
          } } = await getMatches({
            service_id: this.selectedService.id,
            zipcode,
          });
          available = count;
        } catch (err) {
          console.error(err); // eslint-disable-line no-console
        }
      }

      if (available > 0) {
        this.$store.commit('homepage/request/setSelectedServiceAvailable', true);
      } else {
        this.$store.commit('homepage/request/setSelectedServiceAvailable', false);
      }
      this.checkedAvailability = true;
    },
    checkForExternalURL: async function checkForExternalURL (query) {
      const { data: results } = await getSubtasks({
        query,
        size: 10,
      });

      // eslint-disable-next-line no-restricted-syntax
      for (const aResult of Object(results.results)) {
        if (aResult.name === query) {
          return aResult.external_url;
        }
      }
      return null;
    },
    closeModal () {
      this.checkedAvailability = false;
      this.$store.commit('homepage/geo/resetLocations');
      this.$store.dispatch('homepage/request/reset');
      this.$store.dispatch('homepage/search/reset');
      this.$store.dispatch('homepage/hierarchies/reset');
    },
  },
};
</script>
